import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, Chip, Typography } from "@mui/material";
import type { DiscoveredTool } from "../../api/customMcpApi";
import type { ToolMeta } from "./toolCategorize";

interface Props {
  tool: DiscoveredTool;
  meta: ToolMeta;
  checked: boolean;
  onToggle: (upstreamName: string) => void;
  /** A tag chip was clicked: the dialog searches by that tag. */
  onTagClick: (tag: string) => void;
}

/** One tool: checkbox, bold name and read/write badge on one line, the
 *  description under it — all in a single container, so a name can never read
 *  as the title of the neighbouring checkbox. */
export function ToolRow({ tool, meta, checked, onToggle, onTagClick }: Props) {
  const [expanded, setExpanded] = useState(false);
  const descRef = useRef<HTMLSpanElement>(null);
  const name = tool.upstream_name;
  const overflowing = useOverflowsLine(descRef, tool.description, expanded);
  const showName = meta.label !== name;

  return (
    <Box
      data-testid={`tool-row-${name}`}
      sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.75, pl: 1 }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(name)}
        // MUI 9 dropped inputProps on Checkbox; slotProps.input reaches the
        // <input> (same pattern as Marketplace/ComponentSelectionDialog.tsx).
        slotProps={{ input: { "aria-label": name } as React.InputHTMLAttributes<HTMLInputElement> }}
        sx={{ p: 0.5 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {meta.label}
          </Typography>
          {showName && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              {name}
            </Typography>
          )}
          <Chip
            label={meta.kind}
            size="small"
            variant="outlined"
            color={meta.kind === "write" ? "warning" : "default"}
            sx={{ height: 18, fontSize: 11 }}
          />
          {tool.tags?.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              aria-label={`Search tag ${tag}`}
              onClick={() => onTagClick(tag)}
              sx={{ height: 18, fontSize: 11 }}
            />
          ))}
        </Box>
        {tool.description && (
          <Typography
            ref={descRef}
            data-testid={`tool-desc-${name}`}
            data-truncated={String(overflowing && !expanded)}
            variant="caption"
            color="text.secondary"
            title={overflowing && !expanded ? tool.description : undefined}
            sx={
              expanded
                ? { display: "block" }
                : { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
            }
          >
            {tool.description}
          </Typography>
        )}
        {overflowing && (
          <Button
            size="small"
            aria-expanded={expanded}
            sx={{ p: 0, minWidth: 0, fontSize: 11 }}
            onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Show less" : "Show more"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/** Whether the one-line (clamped) description is cut off. Measured, not
 *  guessed from its length: the same text fits a wide dialog and overflows a
 *  narrow one. Only re-measured while collapsed, so "Show less" stays put once
 *  expanded; a ResizeObserver catches the dialog changing width. */
function useOverflowsLine(
  ref: React.RefObject<HTMLElement | null>,
  text: string,
  expanded: boolean,
): boolean {
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth);
    measure();
    // jsdom (tests) has no ResizeObserver; the initial measure still runs.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, text, expanded]);
  return overflowing;
}
